import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BsExclamationCircle, BsSave } from 'react-icons/bs';
import SearchableSelect from '../../components/shared/SearchableSelect';
import { GridSearchInput } from '../../components/shared/GridToolbar';
import {
  LIST_CONSOLE_AG_GRID_CLASS,
  ListConsoleGridSection,
  ListConsolePage,
} from '../../components/shared/ListConsoleLayout';
import '../transaction-monitoring/TransactionMonitoring.css';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { DataGrid, type ColDef, type GridApi } from '../../components/ui/table';
import { showToast } from '../../store/toastStore';
import {
  createErrorCatalogColumnDefs,
  type ErrorCatalogGridContext,
} from './table/columnDefs';
import {
  ERROR_CATALOG_GRID_DEFAULT_COL_DEF,
  errorCatalogGridOptions,
  GRID_PAGE_SIZE,
} from './table/gridOptions';
import { SEARCH_DEBOUNCE_MS } from './constants/constants';
import { filterErrorCatalogRows } from './utils/utils';
import {
  createErrorMapping,
  deleteErrorMapping,
  fetchErrorCatalog,
  fetchErrorMappingTerminals,
  updateErrorMapping,
} from './api/api';
import type { ErrorCatalogRow, ErrorMappingTerminal } from './models/types';
import '../merchants/Merchants.css';

export default function ErrorCodesMapping() {
  const [terminals, setTerminals] = useState<ErrorMappingTerminal[]>([]);
  const [terminalId, setTerminalId] = useState<number | null>(null);
  const [terminalsFailed, setTerminalsFailed] = useState(false);
  const [terminalsLoading, setTerminalsLoading] = useState(true);
  const [rows, setRows] = useState<ErrorCatalogRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [catalogReady, setCatalogReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savingAll, setSavingAll] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const gridApiRef = useRef<GridApi<ErrorCatalogRow> | null>(null);
  const selectedTerminal = terminals.find((t) => t.id === terminalId) ?? null;

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    gridApiRef.current?.paginationGoToFirstPage();
  }, [debouncedSearch, terminalId]);

  useEffect(() => {
    setTerminalsLoading(true);
    fetchErrorMappingTerminals()
      .then((list) => {
        setTerminals(list);
        if (list.length > 0) setTerminalId((cur) => cur ?? list[0].id);
      })
      .catch(() => {
        setTerminalsFailed(true);
        showToast('Failed to load acquirer terminals.', 'error');
      })
      .finally(() => setTerminalsLoading(false));
  }, []);

  const loadCatalog = useCallback(async () => {
    if (terminalId == null) return;
    setLoading(true);
    setCatalogReady(false);
    setLoadError(null);
    setRows([]);
    setDrafts({});
    try {
      const catalog = await fetchErrorCatalog(terminalId);
      setRows(catalog);
      setDrafts(Object.fromEntries(catalog.map((r) => [r.errorCode, r.displayMessage ?? ''])));
      setCatalogReady(true);
    } catch (error: any) {
      setLoadError(error?.message || 'Failed to load error catalog.');
    } finally {
      setLoading(false);
    }
  }, [terminalId]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const isDirty = useCallback(
    (row: ErrorCatalogRow) =>
      (drafts[row.errorCode] ?? '').trim() !== (row.displayMessage ?? '').trim(),
    [drafts],
  );

  const dirtyRows = useMemo(() => rows.filter(isDirty), [rows, isDirty]);
  const unsavedCount = dirtyRows.length;
  const overriddenCount = rows.filter((r) => r.id != null).length;

  const filteredRows = useMemo(
    () => filterErrorCatalogRows(rows, debouncedSearch),
    [rows, debouncedSearch],
  );

  const refreshEditableCells = useCallback(() => {
    gridApiRef.current?.refreshCells({
      force: true,
      columns: ['errorCode', 'displayMessage', 'state'],
    });
  }, []);

  const setDraft = useCallback(
    (errorCode: string, value: string) => {
      setDrafts((cur) => ({ ...cur, [errorCode]: value }));
      window.requestAnimationFrame(() => refreshEditableCells());
    },
    [refreshEditableCells],
  );

  const resetRow = useCallback(
    (row: ErrorCatalogRow) => {
      setDrafts((cur) => ({ ...cur, [row.errorCode]: row.displayMessage ?? '' }));
      window.requestAnimationFrame(() => refreshEditableCells());
    },
    [refreshEditableCells],
  );

  const persist = async (row: ErrorCatalogRow): Promise<ErrorCatalogRow> => {
    const msg = (drafts[row.errorCode] ?? '').trim();
    if (terminalId == null) return row;
    if (msg === '' && row.id != null) {
      await deleteErrorMapping(row.id);
      return { ...row, id: null, displayMessage: '', lastModified: null };
    }
    if (msg !== '' && row.id == null) {
      const created = await createErrorMapping({
        acquirerTerminalId: terminalId,
        errorCode: row.errorCode,
        displayMessage: msg,
        active: true,
      });
      return {
        ...row,
        id: created.id,
        displayMessage: msg,
        lastModified: new Date().toISOString(),
      };
    }
    if (msg !== '' && row.id != null) {
      await updateErrorMapping(row.id, {
        errorCode: row.errorCode,
        displayMessage: msg,
        active: row.active,
      });
      return { ...row, displayMessage: msg, lastModified: new Date().toISOString() };
    }
    return { ...row, displayMessage: '' };
  };

  const saveRow = useCallback(
    async (row: ErrorCatalogRow) => {
      setSavingKey(row.errorCode);
      try {
        const updated = await persist(row);
        setRows((cur) => cur.map((r) => (r.errorCode === row.errorCode ? updated : r)));
        setDrafts((cur) => ({ ...cur, [row.errorCode]: updated.displayMessage ?? '' }));
        window.requestAnimationFrame(() => refreshEditableCells());
      } catch (error: any) {
        showToast(error?.message || 'Failed to save.', 'error');
      } finally {
        setSavingKey((k) => (k === row.errorCode ? null : k));
      }
    },
    [drafts, terminalId, refreshEditableCells],
  );

  const saveAll = async () => {
    setSavingAll(true);
    try {
      const results = await Promise.all(
        dirtyRows.map(async (row) => ({ code: row.errorCode, updated: await persist(row) })),
      );
      setRows((cur) => cur.map((r) => results.find((x) => x.code === r.errorCode)?.updated ?? r));
      setDrafts((cur) => {
        const next = { ...cur };
        for (const x of results) next[x.code] = x.updated.displayMessage ?? '';
        return next;
      });
      showToast(`Saved ${results.length} change${results.length > 1 ? 's' : ''}.`, 'success');
      window.requestAnimationFrame(() => refreshEditableCells());
    } catch (error: any) {
      showToast(error?.message || 'Failed to save changes.', 'error');
      loadCatalog();
    } finally {
      setSavingAll(false);
    }
  };

  const discardAll = () => {
    setDrafts(Object.fromEntries(rows.map((r) => [r.errorCode, r.displayMessage ?? ''])));
    window.requestAnimationFrame(() => refreshEditableCells());
  };

  const changeTerminal = (value: string) => {
    const next = Number(value) || null;
    if (next === terminalId) return;
    if (
      unsavedCount > 0 &&
      !window.confirm('You have unsaved changes. Discard them and switch acquirer?')
    ) {
      return;
    }
    setTerminalId(next);
  };

  const overriddenPct = rows.length > 0 ? Math.round((overriddenCount / rows.length) * 100) : 0;

  const columnDefs = useMemo<ColDef<ErrorCatalogRow>[]>(
    () => createErrorCatalogColumnDefs(),
    [],
  );

  const gridContext = useMemo<ErrorCatalogGridContext>(
    () => ({
      drafts,
      savingKey,
      isDirty,
      onDraftChange: setDraft,
      onSaveRow: saveRow,
      onResetRow: resetRow,
    }),
    [drafts, savingKey, isDirty, setDraft, saveRow, resetRow],
  );

  const handleGridReady = useCallback((event: { api: GridApi<ErrorCatalogRow> }) => {
    gridApiRef.current = event.api;
  }, []);

  const gridLoading = terminalsLoading || loading;

  const gridEmptyMessage =
    !catalogReady || gridLoading
      ? ''
      : rows.length === 0
        ? 'No failed transactions recorded for this terminal yet — nothing to map.'
        : 'No error codes match the current search.';

  return (
    <ListConsolePage
      title="Error Codes Mapping"
      className={unsavedCount > 0 ? 'pb-20' : undefined}
      error={loadError}
    >
      <ListConsoleGridSection
        toolbarLeft={
          <div className="w-72 max-w-full">
            <SearchableSelect
              className="h-9"
              value={terminalId != null ? String(terminalId) : ''}
              options={terminals.map((t) => ({ value: String(t.id), label: t.label }))}
              onChange={changeTerminal}
              placeholder={terminalsFailed ? 'Failed to load terminals' : 'Select acquirer terminal'}
              searchPlaceholder="Search terminal…"
              emptyMessage={terminalsFailed ? 'Failed to load terminals' : 'No terminals available'}
              aria-label="Acquirer terminal"
            />
          </div>
        }
        toolbarRight={
          <div className="tm-toolbar-controls">
            <GridSearchInput
              value={search}
              onValueChange={setSearch}
              placeholder="Search error code, category, or message…"
              className="tm-toolbar-search"
              disabled={gridLoading || rows.length === 0}
              aria-label="Search error codes"
            />
          </div>
        }
      >
        <Card className="overflow-hidden border-0 shadow-none">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
            <p className="text-sm text-muted-foreground">
              Errors returned by{' '}
              <span className="font-medium text-foreground">
                {selectedTerminal ? selectedTerminal.label : 'this terminal'}
              </span>
              . Enter a message to override what the user sees; leave a row blank to keep the
              default.
            </p>
            <div className="flex items-center gap-5">
              {unsavedCount > 0 && (
                <span className="flex items-center gap-1.5 text-xs font-medium text-[#b7791f]">
                  <BsExclamationCircle className="h-3.5 w-3.5" />
                  {unsavedCount} unsaved change{unsavedCount > 1 ? 's' : ''}
                </span>
              )}
              {rows.length > 0 && (
                <div className="flex flex-col items-end gap-1">
                  <span className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{overriddenCount}</span> of{' '}
                    {rows.length} overridden
                  </span>
                  <div className="h-1 w-28 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${overriddenPct}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <DataGrid<ErrorCatalogRow>
            rowData={filteredRows}
            columnDefs={columnDefs}
            context={gridContext}
            defaultColDef={ERROR_CATALOG_GRID_DEFAULT_COL_DEF}
            gridOptions={{
              ...errorCatalogGridOptions,
              overlayNoRowsTemplate: gridEmptyMessage
                ? `<span class="text-sm text-muted-foreground">${gridEmptyMessage}</span>`
                : '<span></span>',
            }}
            includeAuditColumns={false}
            loading={gridLoading}
            pagination
            paginationPageSize={GRID_PAGE_SIZE}
            height="100%"
            className={LIST_CONSOLE_AG_GRID_CLASS}
            onGridReady={handleGridReady}
          />
        </Card>
      </ListConsoleGridSection>

      {unsavedCount > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-card px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.10)]">
          <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-3">
            <span className="text-sm">
              <span className="font-medium text-[#b7791f]">
                {unsavedCount} unsaved change{unsavedCount > 1 ? 's' : ''}
              </span>
              <span className="text-muted-foreground">
                {' '}
                — save or discard before switching acquirers
              </span>
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={discardAll} disabled={savingAll}>
                Discard all
              </Button>
              <Button onClick={saveAll} disabled={savingAll} className="gap-1.5">
                <BsSave className="h-3.5 w-3.5" />
                {savingAll ? 'Saving…' : `Save ${unsavedCount} change${unsavedCount > 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ListConsolePage>
  );
}
